// MARK://dhk8/admin-shell/lib/clients/menus
// Composición de menús → dhk8-catalog-menus (en prod).
//   GET    /v1/menus                                    → { items: MenuDto[], total }
//   GET    /v1/menus/:id                                → MenuDto (con sections + products)
//   POST   /v1/menus            body { name, branchId?, daysOfWeek?, startTime?, endTime? }
//   PATCH  /v1/menus/:id        body { name?, daysOfWeek?, startTime?, endTime?, active? }
//   DELETE /v1/menus/:id
//   POST   /v1/menus/:id/publish
//   POST   /v1/menus/:menuId/sections                   body { name, ordinal? }
//   PATCH  /v1/menus/:menuId/sections/:sectionId        body { name?, ordinal? }
//   DELETE /v1/menus/:menuId/sections/:sectionId
//   POST   /v1/menus/:menuId/sections/:sectionId/products   body { productId, ordinal?, overridePriceCents? }
//   DELETE /v1/menus/:menuId/sections/:sectionId/products/:menuProductId
//
// CONTRACT NOTE: el tenant viaja SOLO por header (`x-dhk8-tenant-id`) — este
// servicio NO acepta `?tenantId=` en query (usa `tenantOf(req)`). Respuestas en
// sobre `{ ok, data }` → apiData.
import { PREVIEW_STUB, mock } from '../env.js'
import { gqlClient } from '../gql.js'
import { AdminMenusQuery, AdminMenuQuery, AdminCreateMenuMutation, AdminUpdateMenuMutation, AdminDeleteMenuMutation, AdminPublishMenuMutation, AdminCreateMenuSectionMutation, AdminUpdateMenuSectionMutation, AdminDeleteMenuSectionMutation, AdminAddMenuProductMutation, AdminRemoveMenuProductMutation } from '../queries/menus.queries.js'


export interface MenuProduct {
  id: string
  sectionId: string
  productId: string
  ordinal: number
  visible: boolean
  overridePriceCents: number | null
}
export interface MenuSection {
  id: string
  menuId: string
  name: string
  ordinal: number
  active: boolean
  products: MenuProduct[]
}
export interface Menu {
  id: string
  tenantId: string
  branchId: string | null
  name: string
  daysOfWeek: number[]
  startTime: string | null
  endTime: string | null
  active: boolean
  publishedAt: string | null
  createdAt: string
  updatedAt: string
  sections: MenuSection[]
}


export const DAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

/** Human label for a menu's schedule window. */
export function scheduleLabel(m: Pick<Menu, 'daysOfWeek' | 'startTime' | 'endTime'>): string {
  const days = m.daysOfWeek.length === 0 || m.daysOfWeek.length === 7
    ? 'Todos los días'
    : m.daysOfWeek.slice().sort((a, b) => a - b).map((d) => DAY_LABELS[d]).join(', ')
  const window = m.startTime && m.endTime ? ` · ${m.startTime}–${m.endTime}` : ''
  return days + window
}

// ── preview store ──────────────────────────────────────────────────────────
const now = () => new Date().toISOString()
const mkid = (p: string) => `${p}-${Math.random().toString(36).slice(2, 8)}`
const db: { menus: Menu[] } = {
  menus: [
    {
      id: 'menu-1', tenantId: 'tnt_demo', branchId: null, name: 'Menú principal',
      daysOfWeek: [], startTime: null, endTime: null, active: true,
      publishedAt: now(), createdAt: now(), updatedAt: now(),
      sections: [
        {
          id: 'sec-1', menuId: 'menu-1', name: 'Bebidas calientes', ordinal: 0, active: true,
          products: [
            { id: 'mp-1', sectionId: 'sec-1', productId: 'p-cap', ordinal: 0, visible: true, overridePriceCents: null },
          ],
        },
      ],
    },
  ],
}

// Mapea el Menu del grafo (typed-document-node) al tipo de UI. Campos 1:1 con el
// SDL de catalog-menus; normalizamos nullables.
interface GqlMenu {
  id: string; tenantId: string; branchId?: string | null; name: string
  daysOfWeek: ReadonlyArray<number>; startTime?: string | null; endTime?: string | null
  active: boolean; publishedAt?: string | null; createdAt: string; updatedAt: string
  sections: ReadonlyArray<{
    id: string; menuId: string; name: string; ordinal: number; active: boolean
    products: ReadonlyArray<{ id: string; sectionId: string; productId: string; ordinal: number; visible: boolean; overridePriceCents?: number | null }>
  }>
}
function toUiMenu(m: GqlMenu): Menu {
  return {
    id: m.id, tenantId: m.tenantId, branchId: m.branchId ?? null, name: m.name,
    daysOfWeek: [...m.daysOfWeek], startTime: m.startTime ?? null, endTime: m.endTime ?? null,
    active: m.active, publishedAt: m.publishedAt ?? null, createdAt: m.createdAt, updatedAt: m.updatedAt,
    sections: m.sections.map((s) => ({
      id: s.id, menuId: s.menuId, name: s.name, ordinal: s.ordinal, active: s.active,
      products: s.products.map((p) => ({
        id: p.id, sectionId: p.sectionId, productId: p.productId,
        ordinal: p.ordinal, visible: p.visible, overridePriceCents: p.overridePriceCents ?? null,
      })),
    })),
  }
}

interface GqlSection { id: string; menuId: string; name: string; ordinal: number; active: boolean; products: ReadonlyArray<{ id: string; sectionId: string; productId: string; ordinal: number; visible: boolean; overridePriceCents?: number | null }> }
function toUiSection(s: GqlSection): MenuSection {
  return {
    id: s.id, menuId: s.menuId, name: s.name, ordinal: s.ordinal, active: s.active,
    products: s.products.map((p) => ({ id: p.id, sectionId: p.sectionId, productId: p.productId, ordinal: p.ordinal, visible: p.visible, overridePriceCents: p.overridePriceCents ?? null })),
  }
}
interface GqlMenuProduct { id: string; sectionId: string; productId: string; ordinal: number; visible: boolean; overridePriceCents?: number | null }
function toUiMenuProduct(p: GqlMenuProduct): MenuProduct {
  return { id: p.id, sectionId: p.sectionId, productId: p.productId, ordinal: p.ordinal, visible: p.visible, overridePriceCents: p.overridePriceCents ?? null }
}

export async function listMenus(signal?: AbortSignal): Promise<Menu[]> {
  if (PREVIEW_STUB) return mock(db.menus)
  // F1.7 / ADR-0009: lectura por el grafo (tenant del JWT, RLS). Escrituras siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminMenusQuery, { limit: 100 }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.menus.items ?? []).map(toUiMenu)
}

export async function getMenu(id: string, signal?: AbortSignal): Promise<Menu> {
  if (PREVIEW_STUB) {
    const m = db.menus.find((x) => x.id === id)
    if (!m) throw new Error('Menú no encontrado')
    return mock(m)
  }
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminMenuQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const m = res.data?.menu
  if (!m) throw new Error('Menú no encontrado')
  return toUiMenu(m)
}

export async function createMenu(input: { name: string; daysOfWeek?: number[]; startTime?: string; endTime?: string }): Promise<Menu> {
  if (PREVIEW_STUB) {
    const m: Menu = {
      id: mkid('menu'), tenantId: 'tnt_demo', branchId: null, name: input.name,
      daysOfWeek: input.daysOfWeek ?? [], startTime: input.startTime ?? null, endTime: input.endTime ?? null,
      active: true, publishedAt: null, createdAt: now(), updatedAt: now(), sections: [],
    }
    db.menus.unshift(m)
    return mock(m)
  }
  // F9.4 / ADR-0009: alta por el grafo (tenant del JWT, RLS). Sin tenantId en el input.
  const res = await gqlClient.mutation(AdminCreateMenuMutation, {
    input: {
      name: input.name,
      ...(input.daysOfWeek ? { daysOfWeek: input.daysOfWeek } : {}),
      ...(input.startTime ? { startTime: input.startTime } : {}),
      ...(input.endTime ? { endTime: input.endTime } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const m = res.data?.createMenu
  if (!m) throw new Error('createMenu: empty response')
  return toUiMenu(m)
}

export async function updateMenu(id: string, patch: { name?: string; daysOfWeek?: number[]; startTime?: string | null; endTime?: string | null; active?: boolean }): Promise<Menu> {
  if (PREVIEW_STUB) {
    const m = db.menus.find((x) => x.id === id)!
    Object.assign(m, patch, { updatedAt: now() })
    return mock(m)
  }
  const res = await gqlClient.mutation(AdminUpdateMenuMutation, {
    id,
    input: {
      ...(patch.name !== undefined ? { name: patch.name } : {}),
      ...(patch.daysOfWeek !== undefined ? { daysOfWeek: patch.daysOfWeek } : {}),
      ...(patch.startTime !== undefined ? { startTime: patch.startTime } : {}),
      ...(patch.endTime !== undefined ? { endTime: patch.endTime } : {}),
      ...(patch.active !== undefined ? { active: patch.active } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const m = res.data?.updateMenu
  if (!m) throw new Error('updateMenu: empty response')
  return toUiMenu(m)
}

export async function deleteMenu(id: string): Promise<void> {
  if (PREVIEW_STUB) { db.menus = db.menus.filter((x) => x.id !== id); return mock(undefined) }
  const res = await gqlClient.mutation(AdminDeleteMenuMutation, { id }).toPromise()
  if (res.error) throw res.error
}

export async function publishMenu(id: string): Promise<Menu> {
  if (PREVIEW_STUB) { const m = db.menus.find((x) => x.id === id)!; m.publishedAt = now(); m.active = true; return mock(m) }
  const res = await gqlClient.mutation(AdminPublishMenuMutation, { id }).toPromise()
  if (res.error) throw res.error
  const m = res.data?.publishMenu
  if (!m) throw new Error('publishMenu: empty response')
  return toUiMenu(m)
}

export async function createSection(menuId: string, name: string, ordinal = 0): Promise<MenuSection> {
  if (PREVIEW_STUB) {
    const m = db.menus.find((x) => x.id === menuId)!
    const s: MenuSection = { id: mkid('sec'), menuId, name, ordinal, active: true, products: [] }
    m.sections.push(s)
    return mock(s)
  }
  const res = await gqlClient.mutation(AdminCreateMenuSectionMutation, { menuId, input: { name, ordinal } }).toPromise()
  if (res.error) throw res.error
  const sct = res.data?.createMenuSection
  if (!sct) throw new Error('createSection: empty response')
  return toUiSection(sct)
}

export async function updateSection(menuId: string, sectionId: string, patch: { name?: string; ordinal?: number }): Promise<MenuSection> {
  if (PREVIEW_STUB) {
    const s = db.menus.find((x) => x.id === menuId)!.sections.find((x) => x.id === sectionId)!
    Object.assign(s, patch)
    return mock(s)
  }
  const res = await gqlClient.mutation(AdminUpdateMenuSectionMutation, {
    menuId,
    sectionId,
    input: {
      ...(patch.name !== undefined ? { name: patch.name } : {}),
      ...(patch.ordinal !== undefined ? { ordinal: patch.ordinal } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const sct = res.data?.updateMenuSection
  if (!sct) throw new Error('updateSection: empty response')
  return toUiSection(sct)
}

export async function removeSection(menuId: string, sectionId: string): Promise<void> {
  if (PREVIEW_STUB) {
    const m = db.menus.find((x) => x.id === menuId)!
    m.sections = m.sections.filter((s) => s.id !== sectionId)
    return mock(undefined)
  }
  const res = await gqlClient.mutation(AdminDeleteMenuSectionMutation, { menuId, sectionId }).toPromise()
  if (res.error) throw res.error
}

export async function addProduct(menuId: string, sectionId: string, input: { productId: string; ordinal?: number; overridePriceCents?: number }): Promise<MenuProduct> {
  if (PREVIEW_STUB) {
    const s = db.menus.find((x) => x.id === menuId)!.sections.find((x) => x.id === sectionId)!
    const p: MenuProduct = { id: mkid('mp'), sectionId, productId: input.productId, ordinal: input.ordinal ?? 0, visible: true, overridePriceCents: input.overridePriceCents ?? null }
    s.products.push(p)
    return mock(p)
  }
  const res = await gqlClient.mutation(AdminAddMenuProductMutation, {
    menuId,
    sectionId,
    input: {
      productId: input.productId,
      ordinal: input.ordinal ?? 0,
      ...(input.overridePriceCents != null ? { overridePriceCents: input.overridePriceCents } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const mp = res.data?.addMenuProduct
  if (!mp) throw new Error('addProduct: empty response')
  return toUiMenuProduct(mp)
}

export async function removeProduct(menuId: string, sectionId: string, menuProductId: string): Promise<void> {
  if (PREVIEW_STUB) {
    const s = db.menus.find((x) => x.id === menuId)!.sections.find((x) => x.id === sectionId)!
    s.products = s.products.filter((p) => p.id !== menuProductId)
    return mock(undefined)
  }
  const res = await gqlClient.mutation(AdminRemoveMenuProductMutation, { menuId, sectionId, menuProductId }).toPromise()
  if (res.error) throw res.error
}
